import loadable from '@loadable/component';
import {
  AccountRecoveryContainer,
  CompareCompanyContainer,
  DashboardContainer,
  DeprecatedPortfolioContainer,
  DeprecatedPortfolioEditTxnContainer,
  DeprecatedPortfolioImportContainer,
  DeprecatedPortfolioReturnsContainer,
  DeprecatedPortfoliosContainer,
  DiscoverHubContainer,
  OffboardingContainer,
  OopsContainer,
  OTPLoginContainer,
  UserContainer,
} from '@/containers';
import type { Components } from '@/reducers/page';
import { shallowEqual, useSelector } from 'react-redux';

const CompanyReport = loadable(() => import('@/pages/CompanyReport'));
const AffiliateProgram = loadable(() => import('@/pages/AffiliateProgram'));
const Article = loadable(() => import('@/pages/Article'));
const Market = loadable(() => import('@/pages/Market'));
const MarketHub = loadable(() => import('@/pages/MarketHub'));
const NotFound = loadable(() => import('@/pages/NotFound'));
const Portfolio = loadable(() => import('@/pages/Portfolio'));
const NewPortfolio = loadable(() => import('@/pages/NewPortfolio'));
const DefaultPortfolio = loadable(() => import('@/pages/DefaultPortfolio'));
const PortfolioLinkingSuccess = loadable(
  () => import('@/pages/PortfolioLinkingSuccess')
);
const DiscoverPageV1 = loadable(() => import('@/pages/DiscoverPageV1'));
const DiscoverPageV2 = loadable(() => import('@/pages/DiscoverPageV2'));
const CommunityIdea = loadable(() => import('@/pages/CommunityIdea'));
const Plans = loadable(() => import('@/pages/Plans'));
const Welcome = loadable(() => import('@/pages/Welcome'));
const TermsAndConditions = loadable(() => import('@/pages/TermsAndConditions'));
const PrivacyPolicy = loadable(() => import('@/pages/PrivacyPolicy'));
const Unsubscribe = loadable(() => import('@/pages/Unsubscribe'));
const Maintenance = loadable(() => import('@/pages/Maintenance'));
const About = loadable(() => import('@/pages/About'));
const Careers = loadable(() => import('@/pages/Careers'));
const Stocks = loadable(() => import('@/pages/Stocks'));
const Screener = loadable(() => import('@/pages/Screener'));
const Podcasts = loadable(() => import('@/pages/Podcasts'));
const Home = loadable(() => import('@/pages/Home'), {
  resolveComponent: (c) => c.Home,
});
const Watchlist = loadable(() => import('@/pages/Watchlist'));
const AccountValidation = loadable(() => import('@/pages/AccountValidation'), {
  resolveComponent: (c) => c.AccountValidation,
});
const Logout = loadable(() => import('@/pages/Logout'), {
  resolveComponent: (c) => c.Logout,
});

const PortfolioLanding = loadable(
  () => import('@/pages/Portfolio/components/Landing'),
  {
    resolveComponent: (c) => c.Landing,
  }
);

const Narrative = loadable(() => import('@/pages/Narrative'));
const NarrativeCreation = loadable(() => import('@/pages/NarrativeCreation'));
const NarrativeEdition = loadable(() => import('@/pages/NarrativeEdition'));
const NarrativeList = loadable(() => import('@/pages/NarrativeList'));
const MagicLink = loadable(() => import('@/pages/MagicLink'));

const Switcher = () => {
  const current = useSelector<
    { page: { current: Components[keyof Components] } },
    Components[keyof Components]
  >((state) => {
    return state.page.current;
  }, shallowEqual);

  switch (current) {
    case 'NotFound':
      return <NotFound />;
    case 'DiscoverHubContainer':
      return <DiscoverHubContainer />;
    case 'DiscoverPageV1':
      return <DiscoverPageV1 />;
    case 'DiscoverPageV2':
      return <DiscoverPageV2 />;
    case 'CommunityIdea':
      return <CommunityIdea />;
    case 'UserNotificationsContainer':
    case 'UserPlanContainer':
    case 'UserBetaLabContainer':
    case 'UserProfileContainer':
    case 'UserSubscriptionContainer':
    case 'UserSubscriptionAndBillingContainer':
    case 'ProApi':
      return <UserContainer />;
    case 'Plans':
      if (RUNTIME_ENV === 'native') {
        return <UserContainer />;
      } else {
        return <Plans />;
      }
    case 'Home':
      return <Home />;
    case 'Careers':
      return <Careers />;
    case 'About':
      return <About />;
    case 'AccountRecoveryContainer':
      return <AccountRecoveryContainer />;
    case 'CompanyReport':
      return <CompanyReport />;
    case 'CompareCompanyContainer':
      return <CompareCompanyContainer />;
    case 'DashboardContainer':
      return <DashboardContainer />;
    case 'Maintenance':
      return <Maintenance />;
    case 'OTPLoginContainer':
      return <OTPLoginContainer />;
    case 'OffboardingContainer':
      return <OffboardingContainer />;
    case 'OopsContainer':
      return <OopsContainer />;
    case 'Podcasts':
      return <Podcasts />;
    case 'DeprecatedPortfolioContainer':
      return <DeprecatedPortfolioContainer />;
    case 'DeprecatedPortfolioEditTxnContainer':
      return <DeprecatedPortfolioEditTxnContainer />;
    case 'DeprecatedPortfolioImportContainer':
      return <DeprecatedPortfolioImportContainer />;
    case 'DeprecatedPortfolioReturnsContainer':
      return <DeprecatedPortfolioReturnsContainer />;
    case 'DeprecatedPortfoliosContainer':
      return <DeprecatedPortfoliosContainer />;
    case 'PrivacyPolicy':
      return <PrivacyPolicy />;
    case 'Screener':
      return <Screener />;
    case 'Stocks':
      return <Stocks />;
    case 'TermsAndConditions':
      return <TermsAndConditions />;
    case 'Watchlist':
      return <Watchlist />;
    case 'Welcome':
      return <Welcome />;
    case 'Article':
      return <Article />;
    case 'MarketHub':
      return <MarketHub />;
    case 'Market':
      return <Market />;
    case 'Unsubscribe':
      return <Unsubscribe />;
    case 'AffiliateProgram':
      return <AffiliateProgram />;
    case 'Portfolio':
      return <Portfolio />;
    case 'NewPortfolio':
      return <NewPortfolio />;
    case 'DefaultPortfolio':
      return <DefaultPortfolio />;
    case 'PortfolioLinkingSuccess':
      return <PortfolioLinkingSuccess />;
    case 'AccountValidation':
      return <AccountValidation />;
    case 'Logout':
      return <Logout />;
    case 'PortfolioLanding':
      return <PortfolioLanding />;
    case 'Narrative':
      return <Narrative />;
    case 'NarrativeCreation':
      return <NarrativeCreation />;
    case 'NarrativeEdition':
      return <NarrativeEdition />;
    case 'NarrativeList':
      return <NarrativeList />;
    case 'MagicLink':
      return <MagicLink />;
    default:
      return <NotFound />;
  }
};
export default Switcher;
